import { useState } from 'react';
import { createFileRoute, useNavigate, Navigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { GlobVector } from '@glob/glob';
import { Button, IconButton, Input, Field } from '@glob/ui';
import { useVaultStore } from '../../features/auth/vaultStore';
import { ensureDefaultSpace } from '../../features/items/data/spaces';
import {
  createItem,
  emptyMemo,
  emptyLink,
  emptySnippet,
  type ItemType,
  type MemoPayload,
  type LinkPayload,
  type SnippetPayload,
  type SnippetFile,
} from '../../features/items/data/items';
import { useT } from '../../i18n';

const TYPES: ReadonlySet<string> = new Set(['link', 'snippet', 'memo']);

function isItemType(value: string): value is ItemType {
  return TYPES.has(value);
}

/**
 * Generic composer for all three item types. Collects a type-specific payload,
 * then creates the item ONCE (client UUIDv7 → encrypt → upload → cache) and
 * navigates to the read view — mirroring the memo composer's collect-then-create
 * approach. The in-place editors (useItemEditor) need an already-cached item, so
 * CREATE deliberately does not reuse them.
 */
function NewItem() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { type } = Route.useParams();
  const vault = useVaultStore((s) => s.vault);
  const userUuid = useVaultStore((s) => s.userUuid);

  const [memo, setMemo] = useState<MemoPayload>(emptyMemo());
  const [link, setLink] = useState<LinkPayload>(emptyLink());
  const [snippet, setSnippet] = useState<SnippetPayload>(emptySnippet());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isItemType(String(type))) {
    return <Navigate to="/app/item/new/$type" params={{ type: 'memo' }} replace />;
  }
  const itemType: ItemType = String(type) as ItemType;
  if (!vault || !userUuid) {
    return <p className="p-8 text-sm text-text-3">{t('items.locked')}</p>;
  }

  const payload = itemType === 'link' ? link : itemType === 'snippet' ? snippet : memo;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const spaceUuid = await ensureDefaultSpace(vault.vk);
      const item = await createItem({ vault, userUuid }, spaceUuid, itemType, payload);
      await queryClient.invalidateQueries({ queryKey: ['items'] });
      await navigate({ to: '/app/item/$itemId', params: { itemId: item.uuid } });
    } catch (err) {
      console.warn('create item failed', err);
      setError(t('items.create-error'));
      setSaving(false);
    }
  };

  const updateFile = (i: number, patch: Partial<SnippetFile>) =>
    setSnippet((s) => ({
      ...s,
      files: s.files.map((f, idx) => (idx === i ? { ...f, ...patch } : f)),
    }));
  const addFile = () =>
    setSnippet((s) => ({
      ...s,
      files: [...s.files, { name: '', language: 'text', content: '' }],
    }));
  const removeFile = (i: number) =>
    setSnippet((s) => ({ ...s, files: s.files.filter((_, idx) => idx !== i) }));

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <button
        type="button"
        onClick={() => void navigate({ to: '/app' })}
        className="flex items-center gap-1.5 text-xs text-text-3 hover:text-text-1 transition-colors"
      >
        <ArrowLeft size={13} aria-hidden /> {t('items.back')}
      </button>

      {itemType === 'memo' && (
        <>
          <Input
            value={memo.title}
            onChange={(e) => setMemo({ ...memo, title: e.target.value })}
            placeholder={t('items.title-placeholder')}
            aria-label={t('items.title-placeholder')}
            className="text-lg"
          />
          <textarea
            value={memo.body_markdown}
            onChange={(e) => setMemo({ ...memo, body_markdown: e.target.value })}
            placeholder={t('items.body-placeholder')}
            aria-label={t('items.body-placeholder')}
            className="w-full h-80 rounded-md border border-border bg-bg-1 px-3 py-2 font-mono text-sm text-text-1 outline-none focus:border-transparent focus:outline-2 focus:outline-focus"
          />
        </>
      )}

      {itemType === 'link' && (
        <div className="space-y-4">
          <Field label={t('items.url-label')}>
            <Input
              type="url"
              value={link.url}
              onChange={(e) => setLink({ ...link, url: e.target.value })}
              placeholder={t('items.url-placeholder')}
            />
          </Field>
          <Input
            value={link.title}
            onChange={(e) => setLink({ ...link, title: e.target.value })}
            placeholder={t('items.link-title-placeholder')}
            aria-label={t('items.link-title-placeholder')}
            className="text-lg"
          />
          <Field label={t('items.description-label')}>
            <textarea
              value={link.description}
              onChange={(e) => setLink({ ...link, description: e.target.value })}
              placeholder={t('items.description-placeholder')}
              aria-label={t('items.description-label')}
              className="w-full h-20 rounded-md border border-border bg-bg-1 px-3 py-2 text-sm text-text-1 outline-none focus:border-transparent focus:outline-2 focus:outline-focus"
            />
          </Field>
          <Field label={t('items.notes-label')}>
            <textarea
              value={link.notes}
              onChange={(e) => setLink({ ...link, notes: e.target.value })}
              placeholder={t('items.notes-placeholder')}
              aria-label={t('items.notes-label')}
              className="w-full h-32 rounded-md border border-border bg-bg-1 px-3 py-2 font-mono text-sm text-text-1 outline-none focus:border-transparent focus:outline-2 focus:outline-focus"
            />
          </Field>
        </div>
      )}

      {itemType === 'snippet' && (
        <div className="space-y-4">
          <Input
            value={snippet.title}
            onChange={(e) => setSnippet({ ...snippet, title: e.target.value })}
            placeholder={t('items.snippet-title-placeholder')}
            aria-label={t('items.snippet-title-placeholder')}
            className="text-lg"
          />
          <Field label={t('items.description-label')}>
            <textarea
              value={snippet.description}
              onChange={(e) => setSnippet({ ...snippet, description: e.target.value })}
              placeholder={t('items.description-placeholder')}
              aria-label={t('items.description-label')}
              className="w-full h-16 rounded-md border border-border bg-bg-1 px-3 py-2 text-sm text-text-1 outline-none focus:border-transparent focus:outline-2 focus:outline-focus"
            />
          </Field>
          {snippet.files.map((file, i) => (
            <div key={i} className="rounded-lg border border-border bg-bg-1 p-3 space-y-2">
              <div className="flex gap-2 items-center">
                <Input
                  value={file.name}
                  onChange={(e) => updateFile(i, { name: e.target.value })}
                  placeholder={t('items.filename-placeholder')}
                  aria-label={t('items.filename-placeholder')}
                  className="flex-1 font-mono text-sm"
                />
                <Input
                  value={file.language}
                  onChange={(e) => updateFile(i, { language: e.target.value })}
                  placeholder={t('items.language-label')}
                  aria-label={t('items.language-label')}
                  className="w-32 font-mono text-sm"
                />
                {snippet.files.length > 1 && (
                  <IconButton
                    type="button"
                    aria-label={t('items.delete')}
                    onClick={() => removeFile(i)}
                  >
                    <Trash2 size={15} aria-hidden />
                  </IconButton>
                )}
              </div>
              <textarea
                value={file.content}
                onChange={(e) => updateFile(i, { content: e.target.value })}
                placeholder={t('items.code-placeholder')}
                aria-label={t('items.code-placeholder')}
                className="w-full h-40 rounded-md border border-border bg-bg-2 px-3 py-2 font-mono text-sm text-text-1 outline-none focus:border-transparent focus:outline-2 focus:outline-focus"
              />
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={addFile}>
            <Plus size={14} aria-hidden /> {t('items.add-file')}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? t('items.saving') : t('items.save')}
        </Button>
        {saving && <GlobVector mood="save" size={28} />}
        {error && (
          <span role="alert" className="text-sm text-error">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authed/app/item/new/$type')({
  component: NewItem,
});
